import React from "react";
import {
  Usuario,
  CATEGORIA_LABELS,
  categoriaRequerRegistro,
} from "../../../types";
import { theme } from "../../../shared/styles/theme";
import {
  Crown,
  User,
  Eye,
  Stethoscope,
  Syringe,
  Brain,
  Salad,
  Pill,
  Briefcase,
  Shirt,
  Pencil,
  Lock,
  CheckCircle,
  Trash2,
} from "lucide-react";

interface UsuarioCardProps {
  usuario: Usuario;
  usuarioLogadoId: string;
  onEditar: (usuario: Usuario) => void;
  onExcluir: (id: string) => void;
  onBloquear: (id: string, bloquear: boolean) => void;
}

export default function UsuarioCard({
  usuario,
  usuarioLogadoId,
  onEditar,
  onExcluir,
  onBloquear,
}: UsuarioCardProps) {
  const isUsuarioLogado = usuario.id === usuarioLogadoId;

  // ========================================
  // CORES POR TIPO DE USUÁRIO
  // ========================================
  const tipoConfig: Record<
    string,
    {
      bg: string;
      border: string;
      text: string;
      icon: React.ElementType;
      label: string;
    }
  > = {
    administrador: {
      bg: theme.primary[50],
      border: theme.primary[200],
      text: theme.primary[700],
      icon: Crown,
      label: "Administrador",
    },
    usuario: {
      bg: "#d1fae5",
      border: "#6ee7b7",
      text: "#10b981",
      icon: User,
      label: "Usuário",
    },
    convidado: {
      bg: "#fef3c7",
      border: "#fcd34d",
      text: "#f59e0b",
      icon: Eye,
      label: "Convidado",
    },
  };

  const config = tipoConfig[usuario.tipo];

  // ========================================
  // ÍCONES POR CATEGORIA PROFISSIONAL
  // ========================================
  const categoriaIcons: Record<string, React.ElementType> = {
    medico: Stethoscope,
    enfermeiro: Syringe,
    psicologo: Brain,
    nutricionista: Salad,
    farmaceutico: Pill,
    administrativo: Briefcase,
    outros: Shirt,
  };

  // ========================================
  // HANDLERS
  // ========================================
  const handleExcluir = () => {
    if (isUsuarioLogado) {
      alert("❌ Você não pode excluir sua própria conta");
      return;
    }

    if (
      window.confirm(
        `Tem certeza que deseja excluir o usuário "${usuario.nome}"?\n\nEsta ação não pode ser desfeita.`
      )
    ) {
      onExcluir(usuario.id);
    }
  };

  const handleBloquear = () => {
    if (isUsuarioLogado) {
      alert("❌ Você não pode bloquear sua própria conta");
      return;
    }

    const acao = usuario.ativo ? "bloquear" : "desbloquear";
    if (
      window.confirm(
        `Tem certeza que deseja ${acao} o usuário "${usuario.nome}"?`
      )
    ) {
      onBloquear(usuario.id, !usuario.ativo);
    }
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.sm,
        border: `2px solid ${usuario.ativo ? theme.gray[200] : "#fee2e2"}`,
        padding: "20px",
        position: "relative",
        opacity: usuario.ativo ? 1 : 0.7,
      }}
    >
      {/* BADGE VOCÊ */}
      {isUsuarioLogado && (
        <div
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: theme.primary[600],
            color: "white",
            padding: "4px 12px",
            borderRadius: theme.borderRadius.full,
            fontSize: "11px",
            fontWeight: "bold",
            fontFamily: theme.fonts.sans,
          }}
        >
          VOCÊ
        </div>
      )}

      {/* BADGE BLOQUEADO */}
      {!usuario.ativo && (
        <div
          style={{
            position: "absolute",
            top: "12px",
            right: isUsuarioLogado ? "80px" : "12px",
            background: "#dc2626",
            color: "white",
            padding: "4px 12px",
            borderRadius: theme.borderRadius.full,
            fontSize: "11px",
            fontWeight: "bold",
            fontFamily: theme.fonts.sans,
          }}
        >
          <Lock size={12} style={{ marginRight: "4px" }} /> BLOQUEADO
        </div>
      )}

      {/* HEADER - TIPO E NOME */}
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "inline-block",
            background: config.bg,
            border: `1px solid ${config.border}`,
            color: config.text,
            padding: "6px 12px",
            borderRadius: theme.borderRadius.full,
            fontSize: "12px",
            fontWeight: "600",
            marginBottom: "12px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <config.icon size={14} style={{ marginRight: "6px" }} />{" "}
          {config.label}
        </div>

        <h3
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            color: theme.gray[800],
            margin: "0 0 4px 0",
            fontFamily: theme.fonts.sans,
          }}
        >
          {usuario.nome}
        </h3>

        <p
          style={{
            fontSize: "14px",
            color: theme.gray[600],
            margin: 0,
            fontFamily: theme.fonts.sans,
          }}
        >
          {usuario.email}
        </p>
      </div>

      {/* ⭐ CATEGORIA PROFISSIONAL */}
      <div
        style={{
          background: theme.primary[50],
          border: `1px solid ${theme.primary[200]}`,
          borderRadius: theme.borderRadius.md,
          padding: "12px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: usuario.registroProfissional ? "8px" : "0",
          }}
        >
          {React.createElement(categoriaIcons[usuario.categoria || "medico"], {
            size: 20,
          })}
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: theme.primary[900],
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              {usuario.categoria
                ? CATEGORIA_LABELS[usuario.categoria]
                : "Não definido"}
            </p>

            {/* ⭐ MOSTRAR REGISTRO PROFISSIONAL */}
            {usuario.registroProfissional && (
              <p
                style={{
                  fontSize: "12px",
                  color: theme.primary[700],
                  margin: "4px 0 0 0",
                  fontFamily: theme.fonts.sans,
                  fontWeight: "500",
                }}
              >
                {usuario.registroProfissional.tipo}{" "}
                {usuario.registroProfissional.numero}/
                {usuario.registroProfissional.uf}
              </p>
            )}

          </div>
        </div>
      </div>

      {/* ESPECIALIDADE */}
      {usuario.especialidade && (
        <div style={{ marginBottom: "12px" }}>
          <p
            style={{
              fontSize: "12px",
              color: theme.gray[500],
              margin: "0 0 4px 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            Especialidade:
          </p>
          <p
            style={{
              fontSize: "14px",
              color: theme.gray[700],
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {usuario.especialidade}
          </p>
        </div>
      )}

      {/* CRM LEGADO (mostrar se existir e não tiver registro profissional) */}
      {usuario.crm && !usuario.registroProfissional && (
        <div style={{ marginBottom: "12px" }}>
          <p
            style={{
              fontSize: "12px",
              color: theme.gray[500],
              margin: "0 0 4px 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            CRM (legado):
          </p>
          <p
            style={{
              fontSize: "14px",
              color: theme.gray[700],
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {usuario.crm}
          </p>
        </div>
      )}

      {/* DATA DE CADASTRO */}
      {usuario.criadoEm && (
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              fontSize: "12px",
              color: theme.gray[500],
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            Cadastrado em:{" "}
            {new Date(usuario.criadoEm).toLocaleDateString("pt-BR")}
          </p>
        </div>
      )}

      {/* BOTÕES DE AÇÃO */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          paddingTop: "16px",
          borderTop: `1px solid ${theme.gray[200]}`,
        }}
      >
        <button
          onClick={() => onEditar(usuario)}
          style={{
            flex: 1,
            padding: "10px",
            background: theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.md,
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            fontFamily: theme.fonts.sans,
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.primary[700];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = theme.primary[600];
          }}
        >
          <Pencil size={14} style={{ marginRight: "6px" }} /> Editar
        </button>

        {!isUsuarioLogado && (
          <>
            <button
              onClick={handleBloquear}
              style={{
                flex: 1,
                padding: "10px",
                background: usuario.ativo ? "#f59e0b" : "#10b981",
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
            >
              {usuario.ativo ? (
                <>
                  <Lock size={14} style={{ marginRight: "6px" }} />
                  Bloquear
                </>
              ) : (
                <>
                  <CheckCircle size={14} style={{ marginRight: "6px" }} />
                  Ativar
                </>
              )}
            </button>

            <button
              onClick={handleExcluir}
              style={{
                padding: "10px 16px",
                background: "#dc2626",
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#b91c1c";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#dc2626";
              }}
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
